import { Vector3 } from 'three';
import InstancedShapes from './InstancedShapes.js';

const _axis = new Vector3();
const _tangent = new Vector3();

/**
 * The outer field: shapes scattered through the whole volume around the scene,
 * each tracking its own circular orbit about the origin like a planet.
 *
 * Position is derived from the orbit rather than integrated, so the radius is
 * exact and constant - an instance can never drift into the middle where the
 * text and the central cluster live. Every orbit sits on its own randomly
 * oriented plane, which is what fills the space in all directions instead of
 * banding everything around one equator.
 *
 * Orbits are seeded outside `minRadius` and, loosely following Kepler, the
 * further out a body is the slower it goes.
 */
export default class OrbitField extends InstancedShapes {
    constructor(geometry, material, amount, options, repulsion) {
        super(geometry, material, amount, repulsion);

        const { minRadius, maxRadius, minScale, maxScale, speed } = options;

        this.orbits = [];

        this.place((dummy) => {
            // A random point on the unit sphere gives the orbit's normal, so
            // orbital planes are spread evenly over every orientation.
            const z = Math.random() * 2 - 1;
            const a = Math.random() * Math.PI * 2;
            const r = Math.sqrt(1 - z * z);
            _axis.set(r * Math.cos(a), r * Math.sin(a), z);

            // Any vector not parallel to the normal will do to start the basis.
            _tangent.set(0, 0, 1);
            if (Math.abs(_axis.z) > 0.9) _tangent.set(1, 0, 0);

            const u = new Vector3().crossVectors(_axis, _tangent).normalize();
            const v = new Vector3().crossVectors(_axis, u).normalize();

            const radius = minRadius + Math.random() * (maxRadius - minRadius);

            const orbit = {
                u,
                v,
                radius,
                angle: Math.random() * Math.PI * 2,
                // Kepler-ish falloff, half of them running the other way round.
                angularSpeed: (speed / Math.pow(radius / minRadius, 1.5)) * (Math.random() < 0.5 ? -1 : 1),
                spin: new Vector3(
                    (Math.random() - 0.5) * 0.6,
                    (Math.random() - 0.5) * 0.6,
                    (Math.random() - 0.5) * 0.6
                )
            };

            this.orbits.push(orbit);

            dummy.rotation.set(
                Math.random() * Math.PI,
                Math.random() * Math.PI,
                Math.random() * Math.PI
            );
            const s = minScale + Math.random() * (maxScale - minScale);
            dummy.scale.set(s, s, s);

            this.positionOn(dummy, orbit);
        });
    }

    positionOn(dummy, orbit) {
        const cos = Math.cos(orbit.angle);
        const sin = Math.sin(orbit.angle);
        dummy.position.set(
            (orbit.u.x * cos + orbit.v.x * sin) * orbit.radius,
            (orbit.u.y * cos + orbit.v.y * sin) * orbit.radius,
            (orbit.u.z * cos + orbit.v.z * sin) * orbit.radius
        );
    }

    update(delta) {
        if (!this.mesh.visible) {
            if (this.repulsor) this.repulsor.reset();
            return;
        }

        for (let i = 0; i < this.dummies.length; i++) {
            const dummy = this.dummies[i];
            const orbit = this.orbits[i];

            orbit.angle += orbit.angularSpeed * delta;
            this.positionOn(dummy, orbit);

            dummy.rotation.x += orbit.spin.x * delta;
            dummy.rotation.y += orbit.spin.y * delta;
            dummy.rotation.z += orbit.spin.z * delta;
        }

        // After the orbits have been placed, so the push is measured against
        // where a body actually is this frame. It never touches the orbit, so
        // a shoved body keeps circling and simply eases back onto its track.
        this.repel(delta);
        this.syncMatrices();
    }
}
